import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie, Shield, BarChart3, Megaphone, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { libraryByCategory, type TrackerDefinition } from '@/lib/cookieLibrary';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCookieConsent, UserPreferences } from '@/hooks/useCookieConsent';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';
import { HOME_THEME, MONO, SANS } from "@/components/home-a/theme";

const cookieTranslations = {
  el: {
    kicker: 'Cookies',
    bannerTitle: 'Εσείς αποφασίζετε',
    bannerDescription: 'Τα απαραίτητα cookies κρατούν το site σε λειτουργία. Με τη συγκατάθεσή σας μετράμε επίσης ποιες σελίδες βοηθούν, για να τις βελτιώνουμε. Δεν πουλάμε δεδομένα.',
    policy: 'Πολιτική cookies',
    reject: 'Απόρριψη',
    accept: 'Αποδοχή όλων',
    customize: 'Επιλέξτε τι επιτρέπετε',
    preferencesTitle: 'Προτιμήσεις Cookies',
    preferencesDescription: 'Διαχειριστείτε τις προτιμήσεις cookies παρακάτω. Μπορείτε να αλλάξετε αυτές τις ρυθμίσεις ανά πάσα στιγμή.',
    necessary: 'Απαραίτητα',
    necessaryDesc: 'Κρατούν το site ασφαλές, σας κρατούν συνδεδεμένους και θυμούνται την επιλογή σας για τα cookies και τις ρυθμίσεις της εφαρμογής. Πάντα ενεργά.',
    analytics: 'Ανάλυση',
    analyticsDesc: 'Μας δείχνουν ποιες σελίδες βοηθούν και πού κολλάνε οι επισκέπτες (Google Analytics, Microsoft Clarity και η δική μας μέτρηση).',
    marketing: 'Μάρκετινγκ',
    marketingDesc: 'Μετρούν τη διαφήμισή μας και επιτρέπουν σε Google και Microsoft να συνδέουν επισκέψεις στις υπηρεσίες τους.',
    details: 'Τι περιλαμβάνει',
    thirdParty: 'τρίτου μέρους',
    rejectAll: 'Απόρριψη Όλων',
    acceptAll: 'Αποδοχή Όλων',
    savePreferences: 'Αποθήκευση Προτιμήσεων',
  },
  en: {
    kicker: 'Cookies',
    bannerTitle: 'Your choice, either way',
    bannerDescription: 'Necessary cookies keep the site working. With your OK we also measure which pages help, so we can improve them. We never sell data.',
    policy: 'Cookie policy',
    reject: 'Reject',
    accept: 'Accept all',
    customize: 'Choose what to allow',
    preferencesTitle: 'Cookie Preferences',
    preferencesDescription: 'Manage your cookie preferences below. You can change these settings at any time.',
    necessary: 'Necessary',
    necessaryDesc: 'Keep the site secure, keep you signed in, and remember your cookie choice and the settings you pick in the app. Always on.',
    analytics: 'Analytics',
    analyticsDesc: 'Show us which pages help and where visitors get stuck (Google Analytics, Microsoft Clarity and our own visit counter).',
    marketing: 'Marketing',
    marketingDesc: 'Measure our advertising and let Google and Microsoft link visits across their services.',
    details: 'What this covers',
    thirdParty: 'third party',
    rejectAll: 'Reject All',
    acceptAll: 'Accept All',
    savePreferences: 'Save Preferences',
  },
};

export const CookieConsent = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const t = cookieTranslations[language] || cookieTranslations.en;
  const {
    hasConsented,
    preferences,
    showBanner,
    showModal,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
    reopenBanner,
  } = useCookieConsent();

  const [localPrefs, setLocalPrefs] = useState<UserPreferences>(preferences);

  // Hide cookie banner entirely on account/dashboard/admin pages
  const isAccountArea = location.pathname.startsWith('/account') || 
                        location.pathname.startsWith('/dashboard') ||
                        location.pathname.startsWith('/admin') ||
                        location.pathname.startsWith('/projects');

  // Update local prefs when modal opens
  const handleOpenPreferences = () => {
    setLocalPrefs(preferences);
    openPreferences();
  };

  // The footer's "Cookie settings" link reopens the preferences from any page.
  useEffect(() => {
    const open = () => {
      setLocalPrefs(preferences);
      openPreferences();
    };
    window.addEventListener("ec:cookie-settings", open);
    return () => window.removeEventListener("ec:cookie-settings", open);
  }, [preferences, openPreferences]);

  const handleSavePreferences = () => {
    savePreferences(localPrefs);
  };

  // Don't render anything until we know consent state
  if (hasConsented === null) return null;

  // Don't render on account/dashboard pages at all
  if (isAccountArea) return null;

  const btnBase = "inline-flex h-11 items-center justify-center rounded-[10px] px-4 text-[14px] font-semibold transition-colors";
  const btnOutline = cn(btnBase, "border border-[hsl(var(--ec-line-strong))] bg-foreground/[0.02] text-foreground hover:border-[hsl(var(--ec-mint)/0.5)] hover:bg-primary/[0.06]");

  return (
    <>
      {showBanner && !showModal && (
        <div
          style={{ ...HOME_THEME, fontFamily: SANS }}
          role="dialog"
          aria-live="polite"
          aria-labelledby="cookie-title"
          aria-describedby="cookie-desc"
          className={cn(
            "fixed inset-x-3 bottom-3 z-[9999] text-foreground sm:inset-x-auto sm:bottom-6 sm:left-6 sm:w-[420px]",
            !isMobile && "animate-in fade-in slide-in-from-bottom-4 duration-500",
          )}
        >
          <div className="relative overflow-hidden rounded-[20px] border border-[hsl(var(--ec-line-strong))] bg-card/95 p-5 shadow-[0_30px_80px_-24px_rgba(0,0,0,0.9)] backdrop-blur-xl">
            <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full bg-[radial-gradient(circle,hsl(var(--primary)/0.2),transparent_70%)]" />
            <div className="relative">
              <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>
                <span className="grid h-6 w-6 place-items-center rounded-[7px] border border-[hsl(var(--ec-mint)/0.3)] bg-primary/10">
                  <Cookie className="h-3.5 w-3.5" aria-hidden />
                </span>
                {t.kicker}
              </p>
              <h3 id="cookie-title" className="mt-3 text-[18px] font-semibold tracking-[-0.01em]">{t.bannerTitle}</h3>
              <p id="cookie-desc" className="mt-1.5 text-[13.5px] leading-relaxed text-[hsl(var(--ec-text-2))]">
                {t.bannerDescription}{' '}
                <Link to="/cookies" className="text-[hsl(var(--ec-mint))] underline-offset-2 hover:underline">{t.policy}</Link>
              </p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button type="button" onClick={rejectAll} className={btnOutline}>{t.reject}</button>
                <button type="button" onClick={acceptAll} className={cn(btnBase, "ec-cta")}>{t.accept}</button>
              </div>
              <button
                type="button"
                onClick={handleOpenPreferences}
                className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-[hsl(var(--ec-muted))] transition-colors hover:text-foreground"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
                {t.customize}
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={(open) => !open && closePreferences()}>
        <DialogContent
          style={{ ...HOME_THEME, fontFamily: SANS }}
          className="max-h-[90vh] gap-0 overflow-y-auto rounded-[20px] border-[hsl(var(--ec-line-strong))] bg-card p-0 text-foreground sm:max-w-lg"
        >
          <DialogHeader className="border-b border-border p-6 pb-5 text-left">
            <p className="text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>{t.kicker}</p>
            <DialogTitle className="mt-1 text-[20px] font-semibold tracking-[-0.01em]">{t.preferencesTitle}</DialogTitle>
            <DialogDescription className="text-[13.5px] leading-relaxed text-[hsl(var(--ec-text-2))]">{t.preferencesDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2.5 p-6">
            <CookieCategory icon={Shield} title={t.necessary} description={t.necessaryDesc} items={LIBRARY.necessary} detailsLabel={t.details} thirdPartyLabel={t.thirdParty} checked disabled onChange={() => {}} />
            <CookieCategory icon={BarChart3} title={t.analytics} description={t.analyticsDesc} items={LIBRARY.analytics} detailsLabel={t.details} thirdPartyLabel={t.thirdParty} checked={localPrefs.analytics} onChange={(checked) => setLocalPrefs(prev => ({ ...prev, analytics: checked }))} />
            <CookieCategory icon={Megaphone} title={t.marketing} description={t.marketingDesc} items={LIBRARY.marketing} detailsLabel={t.details} thirdPartyLabel={t.thirdParty} checked={localPrefs.marketing} onChange={(checked) => setLocalPrefs(prev => ({ ...prev, marketing: checked }))} />
          </div>

          <div className="grid grid-cols-1 gap-2 border-t border-border bg-background/40 p-4 sm:grid-cols-3">
            <button type="button" onClick={rejectAll} className={btnOutline}>{t.rejectAll}</button>
            <button type="button" onClick={acceptAll} className={btnOutline}>{t.acceptAll}</button>
            <button type="button" onClick={handleSavePreferences} className={cn(btnBase, "ec-cta")}>{t.savePreferences}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// What each category covers, from the same list the Cookie Policy prints.
const LIBRARY = libraryByCategory();

interface CookieCategoryProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  items: TrackerDefinition[];
  detailsLabel: string;
  thirdPartyLabel: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

const CookieCategory = ({
  icon: Icon,
  title,
  description,
  items,
  detailsLabel,
  thirdPartyLabel,
  checked,
  disabled = false,
  onChange,
}: CookieCategoryProps) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn(
      "rounded-[14px] border p-4 transition-colors",
      checked && !disabled ? "border-[hsl(var(--ec-mint)/0.3)] bg-primary/[0.05]" : "border-border bg-foreground/[0.015]",
    )}>
      <label className={cn("flex items-start gap-3.5", disabled ? "cursor-default" : "cursor-pointer")}>
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] border border-border bg-foreground/[0.03] text-[hsl(var(--ec-mint))]">
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-4">
            <span className="text-[15px] font-medium text-foreground">
              {title}
              {disabled && <span className="ml-2 text-[10.5px] uppercase tracking-wider text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>Always on</span>}
            </span>
            <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} className="shrink-0" aria-label={title} />
          </span>
          <span className="mt-1 block text-[13px] leading-relaxed text-[hsl(var(--ec-text-2))]">{description}</span>
        </span>
      </label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="ml-[50px] mt-2 inline-flex items-center gap-1 text-[12.5px] text-[hsl(var(--ec-muted))] transition-colors hover:text-foreground"
      >
        {detailsLabel} ({items.length})
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} aria-hidden />
      </button>
      {open && (
        <ul className="ml-[50px] mt-2 space-y-2 border-l border-border pl-3">
          {items.map((c) => (
            <li key={`${c.kind}:${c.name}`} className="text-[12.5px] leading-snug">
              <span className="font-medium text-foreground" style={{ fontFamily: MONO }}>{c.name}</span>
              <span className="text-[hsl(var(--ec-muted))]"> · {c.provider}{c.thirdParty ? ` (${thirdPartyLabel})` : ''} · {c.duration}</span>
              <span className="block text-[hsl(var(--ec-text-2))]">{c.purpose}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CookieConsent;
